// "My Interviews" — for any employee assigned as interviewer on a round.
// Bypasses the HR-only RBAC on /api/recruitment by going through the
// dedicated /api/interview-rounds endpoints. Interviewers see:
//   · their own rounds across all candidates
//   · per-round candidate context (resume basics + JD + ALL rounds incl. peers)
//   · ability to write feedback + mark Pass/Fail/Hold on their assigned round

import React, { useEffect, useState } from "react";
import { Calendar, Clock, CheckCircle2, AlertCircle, FileText, ArrowLeft, Save, User, Eye } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Cd, Av, Bg, Bt } from "../components";
import { notify, confirmDialog } from "../notify";
import { CandidateDetail } from "./Recruitment";

const OUTCOMES = ["Scheduled", "Pass", "Fail", "Hold"];
const OUTCOME_COLOR = { Scheduled: K.bl, Pass: K.g, Fail: K.r, Hold: K.a };
// Display-only labels — stored value stays Pass/Fail.
const OUTCOME_LABEL = { Scheduled: "Scheduled", Pass: "Selected", Fail: "Not Selected", Hold: "Hold" };

const fmtDateTime = (s) => s ? new Date(s).toLocaleString('en-IN') : "Time TBD";
const fmtMoney = (v) => v >= 100000 ? "₹" + (v / 100000).toFixed(1) + "L" : v ? "₹" + Number(v).toLocaleString('en-IN') : "—";

export const MyInterviewsPage = () => {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRoundId, setOpenRoundId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/api/interview-rounds/my").then(d => {
      setRounds(Array.isArray(d) ? d : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  if (openRoundId) {
    return <RoundDetail roundId={openRoundId} onBack={() => { setOpenRoundId(null); load(); }} />;
  }

  // Once an interviewer submits any final outcome, the round is locked —
  // so anything past "Scheduled" sits in the Completed bucket.
  const pending = rounds.filter(r => r.outcome === "Scheduled");
  const done = rounds.filter(r => r.outcome !== "Scheduled");

  return <div>
    <div className="flex justify-between items-center mb-[14px]">
      <h1 className="m-0 text-2xl font-extrabold text-t0">My Interviews</h1>
      <div className="text-[14px] text-t3">{rounds.length} round{rounds.length === 1 ? "" : "s"} assigned</div>
    </div>

    {loading ? <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div> :
     rounds.length === 0 ? <Cd><div className="text-center p-[30px] text-t3 text-[14px]">No interviews assigned to you. HR will add you to a round when needed.</div></Cd> :
     <>
       <div className="text-[14px] text-t3 uppercase mb-2">Pending feedback ({pending.length})</div>
       {pending.length === 0
         ? <Cd style={{ marginBottom: 16 }}><div className="text-[14px] text-t3 italic p-2">All caught up.</div></Cd>
         : <div className="space-y-2 mb-5">{pending.map(r => <RoundCard key={r.round_id} r={r} onOpen={() => setOpenRoundId(r.round_id)} />)}</div>
       }
       <div className="text-[14px] text-t3 uppercase mb-2">Completed ({done.length})</div>
       <div className="space-y-2">{done.map(r => <RoundCard key={r.round_id} r={r} onOpen={() => setOpenRoundId(r.round_id)} />)}</div>
     </>}
  </div>;
};

const RoundCard = ({ r, onOpen }) => {
  const c = OUTCOME_COLOR[r.outcome] || K.t3;
  return <Cd>
    <div className="flex items-center gap-3 flex-wrap">
      <Av nm={r.candidate_name} sz={36} />
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-bold text-t0 truncate">{r.candidate_name}</div>
        <div className="text-[14px] text-t3 truncate">{r.req_position || r.position}{r.request_id ? ` · Req #${r.request_id}` : ""}</div>
        <div className="flex items-center gap-2 mt-1 text-[14px] text-t2">
          <Calendar size={10} />{fmtDateTime(r.scheduled_at)}
          <span className="text-t3">·</span>
          <span className="font-semibold text-t1">{r.round_name}</span>
          {r.interview_mode && <><span className="text-t3">·</span><span>{r.interview_mode}</span></>}
        </div>
      </div>
      <Bg c={c} sm>{OUTCOME_LABEL[r.outcome] || r.outcome}</Bg>
      <Bt v="ghost" s="sm" onClick={onOpen}>{r.outcome === "Scheduled" ? "Submit Feedback" : "View"}</Bt>
    </div>
  </Cd>;
};

const RoundDetail = ({ roundId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState("Scheduled");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  // View the full candidate profile without leaving the round. The
  // recruitment router's guard was widened so interviewers assigned to
  // any round can read /api/recruitment/:candId in read-only mode.
  const [openCand, setOpenCand] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/api/interview-rounds/${roundId}/context`)
      .then(d => {
        setData(d);
        setOutcome(d.round.outcome);
        setFeedback(d.round.feedback || "");
        setLoading(false);
      })
      .catch(e => { notify(e.message); setLoading(false); });
  };
  useEffect(load, [roundId]);

  const flash = (text, kind = "ok") => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 2500); };

  const save = async () => {
    if (outcome === "Scheduled") {
      return flash("Pick Pass / Fail / Hold before saving", "err");
    }
    if (!(await confirmDialog("Once submitted, you can't edit this feedback. Continue?"))) return;
    setSaving(true);
    try {
      await api.put(`/api/interview-rounds/${roundId}`, { outcome, feedback });
      notify(`Feedback submitted — marked as ${OUTCOME_LABEL[outcome] || outcome}`, { title: "Saved", variant: "success" });
      // Submission is final — return to the list. Avoids letting the user
      // attempt to "edit" something that is now server-locked.
      onBack();
    } catch (e) { flash(e.message || "Save failed", "err"); notify(e.message || "Failed to submit feedback", { title: "Error", variant: "error" }); setSaving(false); }
  };

  if (loading || !data) return <div className="text-center p-[30px] text-t3 text-[14px]">Loading…</div>;

  const cand = data.candidate;
  const req = data.requisition;

  return <div>
    <div className="mb-3">
      <Bt v="ghost" s="sm" icon={ArrowLeft} onClick={onBack}>Back to my interviews</Bt>
    </div>

    {msg && <div className={`px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold ${msg.kind === "err" ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{msg.text}</div>}

    {/* Candidate header */}
    <Cd style={{ marginBottom: 12 }}>
      <div className="flex items-center gap-3 flex-wrap">
        <Av nm={cand.name} sz={48} />
        <div className="flex-1 min-w-0">
          <div className="text-[17px] font-extrabold text-t0">{cand.name}</div>
          <div className="text-[14px] text-t2">{cand.position}{req ? ` · Requisition #${req.id}` : ""}</div>
          <div className="flex flex-wrap gap-3 mt-1 text-[14px] text-t3">
            {cand.experienceYears != null && <span>Experience: <strong className="text-t1">{cand.experienceYears} yr</strong></span>}
            {cand.currentCompany && <span>Current: <strong className="text-t1">{cand.currentCompany}</strong></span>}
            {cand.expectedCtc && <span>Expected CTC: <strong className="text-t1">{fmtMoney(cand.expectedCtc)}</strong></span>}
          </div>
        </div>
        {cand.id && <Bt v="secondary" s="sm" icon={Eye} onClick={() => setOpenCand(cand.id)}>View Profile</Bt>}
      </div>
      {req?.jdFilePath && <a href={`${BASE}${req.jdFilePath}`} target="_blank" rel="noreferrer" className="mt-3 flex items-center gap-2 bg-bg2 border border-b0 rounded-lg p-2 text-[14px] text-p font-semibold no-underline">
        <FileText size={14} color={K.p} />
        <span className="flex-1 truncate">Job Description: {req.jdFileName || "Open JD"}</span>
        <span className="text-[12px] text-t3 uppercase">Open</span>
      </a>}
      {req?.skills && <div className="mt-2 p-2 bg-bg2 rounded-lg border border-b0">
        <div className="text-[12px] text-t3 uppercase mb-1">Skills required</div>
        <div className="text-[14px] text-t1 whitespace-pre-wrap">{req.skills}</div>
      </div>}
    </Cd>

    {/* All rounds for context */}
    <Cd style={{ marginBottom: 12 }}>
      <div className="text-[14px] text-t3 uppercase mb-2">All Interview Rounds for this Candidate</div>
      <div className="space-y-2">
        {data.allRounds.map(r => {
          const c = OUTCOME_COLOR[r.outcome] || K.t3;
          const Icon = r.outcome === "Pass" ? CheckCircle2 : r.outcome === "Fail" ? AlertCircle : Clock;
          return <div key={r.id} className={`p-3 rounded-lg border ${r.isMine ? "border-p bg-bg2" : "border-b0 bg-bg1"}`}>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Icon size={14} color={c} />
              <div className="text-[14px] font-semibold text-t0 flex-1">{r.name} {r.isMine && <span className="text-[12px] text-p font-bold">(YOUR ROUND)</span>}</div>
              <Bg c={c} sm>{OUTCOME_LABEL[r.outcome] || r.outcome}</Bg>
            </div>
            <div className="text-[14px] text-t3 flex items-center gap-2 flex-wrap">
              <Calendar size={10} />{fmtDateTime(r.scheduledAt)}
              {r.interviewerName && <><span>·</span><User size={10} /><span>{r.interviewerName}</span></>}
              {r.interviewMode && <><span>·</span><span>{r.interviewMode}</span></>}
              {r.interviewMode === "Virtual" && r.virtualLink && <>
                <span>·</span><a href={r.virtualLink} target="_blank" rel="noreferrer" className="text-p font-semibold no-underline">Join Link</a>
              </>}
            </div>
            {r.feedback && <div className="text-[14px] text-t1 mt-2 whitespace-pre-wrap">{r.feedback}</div>}
          </div>;
        })}
      </div>
    </Cd>

    {/* Feedback area — editable until submitted, then read-only forever
        (the submission is the audit record; HR can amend if truly needed) */}
    <Cd>
      <div className="text-[14px] text-t3 uppercase mb-2">Your Feedback for "{data.round.name}"</div>

      {data.round.outcome !== "Scheduled" ? (
        // Locked view — show what was submitted, no editor.
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Bg c={OUTCOME_COLOR[data.round.outcome] || K.t3}>{OUTCOME_LABEL[data.round.outcome] || data.round.outcome}</Bg>
            <span className="text-[14px] text-t3">— Submitted feedback is locked. Contact HR if a correction is needed.</span>
          </div>
          <div className="bg-bg2 border border-b0 rounded-[10px] p-3 text-[14px] text-t1 whitespace-pre-wrap min-h-[60px]">
            {data.round.feedback || <span className="italic text-t3">No written feedback was recorded.</span>}
          </div>
        </div>
      ) : (
        <>
          <div className="text-[14px] text-t3 mb-2">Mark the outcome and add a short rationale. Once submitted, this cannot be edited.</div>

          <div className="flex gap-2 flex-wrap mb-3">
            {OUTCOMES.map(o => <button
              key={o}
              onClick={() => setOutcome(o)}
              className={`px-3 py-2 rounded-lg text-[14px] font-bold cursor-pointer border ${outcome === o ? "" : "opacity-60 hover:opacity-100"}`}
              style={{ background: OUTCOME_COLOR[o] + (outcome === o ? "22" : "10"), color: OUTCOME_COLOR[o], borderColor: OUTCOME_COLOR[o] + (outcome === o ? "" : "33") }}
            >{OUTCOME_LABEL[o]}</button>)}
          </div>

          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="Strengths, concerns, role fit, communication, technical depth…"
            className="w-full min-h-[120px] p-3 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none resize-y"
          />

          <div className="flex justify-end mt-3">
            <Bt v="success" icon={Save} onClick={save} disabled={saving || outcome === "Scheduled"}>{saving ? "Saving…" : "Submit Feedback"}</Bt>
          </div>
        </>
      )}
    </Cd>

    {/* Full candidate profile — interviewers get a read-only view via
         the widened /api/recruitment guard. */}
    {openCand && <CandidateDetail
      candId={openCand}
      onClose={() => setOpenCand(null)}
      onRefresh={() => {}}
      canEdit={false}
      canDelete={false}
    />}
  </div>;
};
